import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Typography,
  Collapse,
  Box,
  Stack,
  Avatar,
  Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

// Fonction utilitaire pour le style des statuts de réservation
const getStatusChip = (status) => {
  let bg = '#FEF3C7';
  let color = '#D97706';
  let label = status || 'Inconnu';
  const lowerStatus = (status || '').toLowerCase();

  if (lowerStatus === 'pending') {
    bg = '#FEF3C7';
    color = '#D97706';
    label = 'En attente';
  } else if (['approved', 'validated', 'valider', 'confirmed', 'confirme', 'confirmé'].includes(lowerStatus)) {
    bg = '#D1FAE5';
    color = '#059669';
    label = 'Confirmé';
  } else if (['cancelled', 'annule', 'annulé'].includes(lowerStatus)) {
    bg = '#FEE2E2';
    color = '#DC2626';
    label = 'Annulé';
  } else if (['completed', 'termine', 'terminé'].includes(lowerStatus)) {
    bg = '#DBEAFE';
    color = '#1D4ED8';
    label = 'Terminé';
  }

  return { bg, color, label };
};

// Fonction utilitaire pour attribuer le bon style selon le statut du Compte Rendu
const getStatutBadgeConfig = (statut) => {
  const st = (statut || '').toLowerCase();
  if (st === 'valide' || st === 'validé') {
    return { label: 'Validé', bg: '#dcfce7', color: '#15803d', border: '#86efac' };
  }
  if (st === 'brouillon' || st === 'draft') {
    return { label: 'Brouillon', bg: '#ffedd5', color: '#c2410c', border: '#fdba74' };
  }
  return { label: statut || 'En attente', bg: '#fef3c7', color: '#b45309', border: '#fde68a' };
};

// Composant pour chaque Ligne d'Offre
const OfferRow = ({ offer, onViewCompteRendu, onEditCompteRendu, onDeleteCompteRendu, onAddCompteRendu, onDownloadPDF }) => {
  const [open, setOpen] = useState(false);

  // --- Calculs des comptes rendus traités vs non traités ---
  let totalComptesRendus = 0;
  let traitesCount = 0;
  let nonTraitesCount = 0;

  offer.reservations.forEach((res) => {
    const crs = res.comptes_rendus || [];
    totalComptesRendus += crs.length;

    if (crs.length === 0) {
      nonTraitesCount += 1; // Réservation sans compte rendu = non traité
    } else {
      crs.forEach((cr) => {
        const st = (cr.statut || '').toLowerCase();
        if (st === 'valide' || st === 'validé') {
          traitesCount += 1;
        } else {
          nonTraitesCount += 1;
        }
      });
    }
  });

  return (
    <>
      {/* LIGNE PRINCIPALE : OFFRE */}
      <TableRow
        hover
        onClick={() => setOpen(!open)}
        sx={{
          cursor: 'pointer',
          backgroundColor: open ? '#f1f5f9' : '#ffffff',
          '& > *': { borderBottom: 'unset' }
        }}
      >
        <TableCell width="50px">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open); }} sx={{ color: '#4f46e5' }}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Offre */}
        <TableCell>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Avatar sx={{ bgcolor: '#e0e7ff', color: '#4f46e5', width: 34, height: 34 }}>
              <LocalOfferOutlinedIcon fontSize="small" />
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: '#0f172a' }}>
                {offer.info_offre?.titre || 'Sans titre'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b' }}>
                Durée : {offer.info_offre?.duree ? `${offer.info_offre.duree} min` : 'N/A'}
              </Typography>
            </Box>
          </Stack>
        </TableCell>

        {/* Date de l'offre */}
        <TableCell sx={{ color: '#475569', fontWeight: 500 }}>
          {offer.info_offre?.date_offre
            ? new Date(offer.info_offre.date_offre).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              })
            : '—'}
        </TableCell>

        {/* Praticien */}
        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>
          {offer.praticien_nom}
        </TableCell>

        {/* Badges Réservations */}
        <TableCell align="center">
          <Chip
            label={`${offer.reservations.length} Réservation(s)`}
            size="small"
            sx={{ fontWeight: 600, backgroundColor: '#f1f5f9', color: '#334155', borderRadius: '8px' }}
          />
        </TableCell>

        {/* Badges Comptes Rendus Globaux (Traités vs Non traités) */}
        <TableCell align="center">
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
            <Tooltip title="Comptes rendus validés / traités">
              <Chip
                label={`Traités : ${traitesCount}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  backgroundColor: '#dcfce7',
                  color: '#15803d',
                  borderRadius: '8px',
                  border: '1px solid #86efac'
                }}
              />
            </Tooltip>

            <Tooltip title="Brouillons ou absents">
              <Chip
                icon={nonTraitesCount > 0 ? <WarningAmberOutlinedIcon style={{ color: '#dc2626', fontSize: '16px' }} /> : undefined}
                label={`À traiter : ${nonTraitesCount}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  backgroundColor: nonTraitesCount > 0 ? '#fee2e2' : '#f1f5f9',
                  color: nonTraitesCount > 0 ? '#dc2626' : '#64748b',
                  borderRadius: '8px',
                  border: `1px solid ${nonTraitesCount > 0 ? '#fca5a5' : '#cbd5e1'}`
                }}
              />
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>

      {/* SOUS-TABLEAU DES RÉSERVATIONS */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2.5, backgroundColor: '#f8fafc', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ color: '#0f172a', mb: 2 }}>
                Détail des réservations et comptes rendus :
              </Typography>

              {offer.reservations && offer.reservations.length > 0 ? (
                <Table size="small" sx={{ backgroundColor: '#ffffff', borderRadius: '8px', overflow: 'hidden' }}>
                  <TableHead sx={{ backgroundColor: '#e2e8f0' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Patient / Client</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Date & Heure Séance</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Statut Réservation</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: '#475569' }}>Comptes Rendus Associés & Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {offer.reservations.map((res) => {
                      const hasComptesRendus = res.comptes_rendus && res.comptes_rendus.length > 0;
                      const statusConfig = getStatusChip(res.status);

                      return (
                        <TableRow key={res.id_offre_reservation}>
                          {/* Client */}
                          <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <PersonOutlineIcon fontSize="small" sx={{ color: '#64748b' }} />
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ color: '#0f172a' }}>
                                  {res.client?.prenom} {res.client?.nom}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                                  {res.client?.email}
                                </Typography>
                              </Box>
                            </Stack>
                          </TableCell>

                          {/* Date */}
                          <TableCell>
                            {res.start_time
                              ? new Date(res.start_time).toLocaleString('fr-FR', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })
                              : 'N/A'}
                          </TableCell>

                          {/* Statut Réservation */}
                          <TableCell>
                            <Chip
                              label={statusConfig.label}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: statusConfig.bg,
                                color: statusConfig.color,
                                borderRadius: '8px'
                              }}
                            />
                          </TableCell>

                          {/* Liste des comptes rendus avec boutons d'action */}
                          <TableCell sx={{ width: '350px' }}>
                            <Stack spacing={1}>
                              {hasComptesRendus ? (
                                res.comptes_rendus.map((cr) => {
                                  const badgeConfig = getStatutBadgeConfig(cr.statut);
                                  const reportId = cr.id_comptes_rendus || cr.id_compte_rendu || cr.id;

                                  return (
                                    <Stack
                                      key={reportId}
                                      direction="row"
                                      alignItems="center"
                                      justifyContent="space-between"
                                      sx={{
                                        p: 1,
                                        borderRadius: '8px',
                                        backgroundColor: badgeConfig.bg,
                                        border: `1px solid ${badgeConfig.border}`
                                      }}
                                    >
                                      <Box>
                                        <Typography variant="caption" fontWeight={700} sx={{ color: badgeConfig.color }}>
                                          {cr.numero_seance} ({badgeConfig.label})
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ color: '#475569' }}>
                                          Date : {cr.date_seance ? new Date(cr.date_seance).toLocaleDateString('fr-FR') : '—'}
                                        </Typography>
                                      </Box>

                                      <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Voir les détails">
                                          <IconButton
                                            size="small"
                                            onClick={() => onViewCompteRendu(cr, offer, res)}
                                            sx={{ color: '#6366F1', backgroundColor: '#ffffff' }}
                                          >
                                            <VisibilityOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Télécharger en PDF">
                                          <IconButton
                                            size="small"
                                            onClick={() => onDownloadPDF(cr, offer, res)}
                                            sx={{ color: '#10B981', backgroundColor: '#ffffff' }}
                                          >
                                            <PictureAsPdfOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Modifier">
                                          <IconButton
                                            size="small"
                                            onClick={() => onEditCompteRendu(cr, offer, res)}
                                            sx={{ color: '#0EA5E9', backgroundColor: '#ffffff' }}
                                          >
                                            <EditOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Supprimer">
                                          <IconButton
                                            size="small"
                                            onClick={() => onDeleteCompteRendu(reportId)}
                                            sx={{ color: '#EF4444', backgroundColor: '#ffffff' }}
                                          >
                                            <DeleteOutlineOutlinedIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </Stack>
                                  );
                                })
                              ) : null}

                              {/* Bouton pour ajouter un nouveau compte rendu */}
                              <Box sx={{ pt: hasComptesRendus ? 0.5 : 0 }}>
                                <Chip
                                  icon={<AddCircleOutlineIcon fontSize="small" />}
                                  label="Rédiger un compte rendu"
                                  onClick={() => onAddCompteRendu(offer, res)}
                                  size="small"
                                  sx={{
                                    backgroundColor: '#e0e7ff',
                                    color: '#4f46e5',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    borderRadius: '8px',
                                    '&:hover': { backgroundColor: '#c7d2fe' }
                                  }}
                                />
                              </Box>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                  Aucune réservation enregistrée.
                </Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const CompteRendusTable = ({ data, onViewCompteRendu, onEditCompteRendu, onDeleteCompteRendu, onAddCompteRendu, onDownloadPDF }) => {
  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        backgroundColor: '#ffffff'
      }}
    >
      <Table>
        <TableHead sx={{ backgroundColor: '#f8fafc' }}>
          <TableRow>
            <TableCell width="50px" />
            <TableCell sx={{ color: '#475569', fontWeight: 700, py: 2 }}>Prestation</TableCell>
            <TableCell sx={{ color: '#475569', fontWeight: 700, py: 2 }}>Date de l'offre</TableCell>
            <TableCell sx={{ color: '#475569', fontWeight: 700, py: 2 }}>Praticien</TableCell>
            <TableCell align="center" sx={{ color: '#475569', fontWeight: 700, py: 2 }}>Réservations</TableCell>
            <TableCell align="center" sx={{ color: '#475569', fontWeight: 700, py: 2 }}>État Comptes Rendus</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data && data.length > 0 ? (
            data.map((offer, index) => (
              <OfferRow
                key={offer.info_offre?.id || index}
                offer={offer}
                onViewCompteRendu={onViewCompteRendu}
                onEditCompteRendu={onEditCompteRendu}
                onDeleteCompteRendu={onDeleteCompteRendu}
                onAddCompteRendu={onAddCompteRendu}
                onDownloadPDF={onDownloadPDF}
              />
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 6, color: '#94a3b8' }}>
                Aucune offre trouvée.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CompteRendusTable;